import * as faceapi from 'face-api.js'

// 使用绝对路径，避免在 /owner/xxx 等非根路由下请求到 /owner/face-api-models 导致 404
const MODEL_URI = '/face-api-models'

/**
 * 根据人脸框计算以人脸为中心的裁剪区域
 * @param {number} sourceWidth 源画布宽度
 * @param {number} sourceHeight 源画布高度
 * @param {object} detection 人脸框 { x, y, width, height }
 * @param {number} outputWidth 输出宽度
 * @param {number} outputHeight 输出高度
 * @param {number} [faceRatio=0.65] 人脸宽度占输出宽度的比例
 * @returns {object} { cropX, cropY, cropWidth, cropHeight }
 */
export function calculateCropRegion(sourceWidth, sourceHeight, detection, outputWidth, outputHeight, faceRatio = 0.65) {
  const { x, y, width, height } = detection
  const faceCenterX = x + width / 2
  const faceCenterY = y + height / 2

  // 让人脸宽度占输出宽度的 faceRatio
  const cropWidth = width / faceRatio
  const cropHeight = cropWidth * (outputHeight / outputWidth)

  let cropX = faceCenterX - cropWidth / 2
  let cropY = faceCenterY - cropHeight / 2

  // 限制在源画布范围内
  cropX = Math.max(0, Math.min(cropX, sourceWidth - cropWidth))
  cropY = Math.max(0, Math.min(cropY, sourceHeight - cropHeight))

  const finalCropWidth = Math.min(cropWidth, sourceWidth - cropX)
  const finalCropHeight = Math.min(cropHeight, sourceHeight - cropY)

  return {
    cropX,
    cropY,
    cropWidth: finalCropWidth,
    cropHeight: finalCropHeight
  }
}

/**
 * 循环降低 JPEG 质量，直到文件大小符合要求
 * @param {HTMLCanvasElement} canvas 待压缩画布
 * @param {number} maxSizeKb 最大文件大小（KB）
 * @returns {Promise<Blob>}
 */
export function compressToSize(canvas, maxSizeKb) {
  return new Promise((resolve, reject) => {
    const maxBytes = maxSizeKb * 1024
    let quality = 0.92

    const tryCompress = () => {
      canvas.toBlob((blob) => {
        if (!blob) {
          reject(new Error('图片转 Blob 失败'))
          return
        }
        if (blob.size <= maxBytes) {
          resolve(blob)
          return
        }
        quality -= 0.05
        if (quality < 0.1) {
          reject(new Error('无法压缩到指定大小，请重新拍摄'))
          return
        }
        tryCompress()
      }, 'image/jpeg', quality)
    }

    tryCompress()
  })
}

/**
 * 加载 TinyFaceDetector 模型，全局缓存
 */
export async function loadModels() {
  if (typeof window !== 'undefined' && window.__faceApiModelsLoaded) {
    return
  }
  try {
    await faceapi.nets.tinyFaceDetector.loadFromUri(MODEL_URI)
    if (typeof window !== 'undefined') {
      window.__faceApiModelsLoaded = true
    }
  } catch (error) {
    console.error('加载人脸识别模型失败:', error)
    // 重置缓存标记，允许下次重新加载
    if (typeof window !== 'undefined') {
      window.__faceApiModelsLoaded = false
    }
    throw new Error('人脸识别模型加载失败，请检查网络或模型文件路径')
  }
}

/**
 * 在视频/图片元素上检测单个人脸
 * @param {HTMLVideoElement|HTMLImageElement|HTMLCanvasElement} input
 * @returns {object|null} { x, y, width, height, score }
 */
export async function detectFace(input) {
  try {
    const detection = await faceapi.detectSingleFace(
      input,
      new faceapi.TinyFaceDetectorOptions({
        inputSize: 416,
        // 适当降低置信度阈值，避免摄像头画质一般或光线不足时始终检测不到人脸
        scoreThreshold: 0.3
      })
    )
    if (!detection) {
      return null
    }
    return {
      x: detection.box.x,
      y: detection.box.y,
      width: detection.box.width,
      height: detection.box.height,
      score: detection.score
    }
  } catch (error) {
    console.error('人脸检测异常:', error)
    return null
  }
}

/**
 * 将图像转换为灰度图
 * @param {HTMLCanvasElement} canvas
 * @returns {HTMLCanvasElement}
 */
export function toGrayscale(canvas) {
  const grayCanvas = document.createElement('canvas')
  grayCanvas.width = canvas.width
  grayCanvas.height = canvas.height
  const ctx = grayCanvas.getContext('2d')
  ctx.drawImage(canvas, 0, 0)
  const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height)
  const data = imageData.data
  for (let i = 0; i < data.length; i += 4) {
    const gray = data[i] * 0.299 + data[i + 1] * 0.587 + data[i + 2] * 0.114
    data[i] = gray
    data[i + 1] = gray
    data[i + 2] = gray
  }
  ctx.putImageData(imageData, 0, 0)
  return grayCanvas
}

/**
 * 用拉普拉斯方差估算图片清晰度
 * @param {HTMLCanvasElement} canvas
 * @returns {number}
 */
export function estimateSharpness(canvas) {
  const grayCanvas = toGrayscale(canvas)
  const ctx = grayCanvas.getContext('2d')
  const width = grayCanvas.width
  const height = grayCanvas.height
  const imageData = ctx.getImageData(0, 0, width, height)
  const data = imageData.data

  let sum = 0
  let count = 0

  for (let y = 1; y < height - 1; y++) {
    for (let x = 1; x < width - 1; x++) {
      const idx = (y * width + x) * 4
      const gray = data[idx]
      const grayLeft = data[idx - 4]
      const grayRight = data[idx + 4]
      const grayTop = data[((y - 1) * width + x) * 4]
      const grayBottom = data[((y + 1) * width + x) * 4]

      const laplacian = 4 * gray - grayLeft - grayRight - grayTop - grayBottom
      sum += laplacian * laplacian
      count++
    }
  }

  return count > 0 ? sum / count : 0
}

/**
 * 以人脸为中心裁剪固定尺寸
 * @param {HTMLCanvasElement} sourceCanvas
 * @param {object} detection 人脸框
 * @param {number} outputWidth
 * @param {number} outputHeight
 * @returns {HTMLCanvasElement}
 */
export function cropFace(sourceCanvas, detection, outputWidth, outputHeight) {
  const region = calculateCropRegion(
    sourceCanvas.width,
    sourceCanvas.height,
    detection,
    outputWidth,
    outputHeight
  )

  const outputCanvas = document.createElement('canvas')
  outputCanvas.width = outputWidth
  outputCanvas.height = outputHeight
  const ctx = outputCanvas.getContext('2d')

  ctx.drawImage(
    sourceCanvas,
    region.cropX,
    region.cropY,
    region.cropWidth,
    region.cropHeight,
    0,
    0,
    outputWidth,
    outputHeight
  )

  return outputCanvas
}

/**
 * 将 File 对象转为 Image
 * @param {File} file
 * @returns {Promise<HTMLImageElement>}
 */
export function fileToImage(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onload = (e) => {
      const img = new Image()
      img.onload = () => resolve(img)
      img.onerror = reject
      img.src = e.target.result
    }
    reader.onerror = reject
    reader.readAsDataURL(file)
  })
}
